import React from "react";

import {
  Close as CloseIcon,
  PriorityHighOutlined as PriorityHighOutlinedIcon,
  ReportOutlined as ReportOutlinedIcon,
} from "@mui/icons-material";
import { darken, IconButton, lighten, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { colors } from "@/theme/overrides/colors";

interface IconProps {
  className?: string;
  color?: "secondary";
}

interface ButtonProps {
  className: string;
  color?: "secondary" | "inherit";
}

interface Props {
  className?: string;
  color?: "secondary" | "error" | "critical";
  description?: string;
  onDismiss?: () => void;
  renderIcon?: (props: IconProps) => React.ReactNode;
  renderLeft?: () => React.ReactNode;
  renderButton?: (props: ButtonProps) => React.ReactNode;
  title?: string;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    borderLeftWidth: theme.spacing(1),
    borderLeftStyle: "solid",
    padding: theme.spacing(5.5, 8, 5.5, 5),
    backgroundColor: colors.white,
  },
  rootCritical: {
    borderLeftColor: "#0B1B26",
    backgroundColor: "#FF6262",
  },
  rootError: {
    borderLeftColor: "#FF6262",
  },
  rootSecondary: {
    borderLeftColor: theme.palette.secondary.main,
  },
  button: {
    flexShrink: 0,
    marginLeft: "auto",
  },
  buttonCritical: {
    backgroundColor: "#0B1B26",
    color: colors.white,
    "&:hover": {
      backgroundColor: darken("#0B1B26", 0.2),
    },
  },
  buttonError: {
    color: colors.white,
    backgroundColor: "#FF6262",
    "&:hover": {
      backgroundColor: darken("#FF6262", 0.2),
    },
  },
  description: {
    color: colors.grey,
    fontSize: 12,
    lineHeight: 1.25,
  },
  descriptionCritical: {
    color: colors.white,
  },
  dismissButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(3),
  },
  dismissButtonContainer: {
    position: "relative",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-start",
    minWidth: theme.spacing(16),
    height: "100%",
    marginTop: theme.spacing(-11),
    marginRight: theme.spacing(-8),
  },
  iconBackground: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginRight: theme.spacing(4),
    padding: theme.spacing(3),
    borderRadius: "50%",
  },
  iconBackgroundError: {
    backgroundColor: "#FF6262",
  },
  iconBackgroundSecondary: {
    backgroundColor: lighten(theme.palette.secondary.main, 0.9),
  },
  iconError: {
    color: colors.white,
  },
  left: {
    marginRight: theme.spacing(4),
  },
  message: {
    marginRight: theme.spacing(4),
    maxWidth: 890,
  },
  title: {
    marginBottom: theme.spacing(1),
    fontSize: 24,
    lineHeight: 1.25,
    fontWeight: 600,
  },
  titleCritical: {
    color: colors.white,
  },
  titleSecondary: {
    color: theme.palette.secondary.main,
  },
  titleError: {
    color: "#FF6262",
  },
}));

const Banner: React.FC<Props> = ({
  className,
  color,
  description,
  renderButton,
  renderIcon,
  renderLeft,
  onDismiss,
  title,
}) => {
  const { classes, cx } = useStyles();

  const iconProps: IconProps = {
    className: color === "error" ? classes.iconError : undefined,
    color: color === "secondary" ? color : undefined,
  };

  const buttonProps: ButtonProps = {
    className: cx(classes.button, {
      [classes.buttonError]: color === "error",
      [classes.buttonCritical]: color === "critical",
    }),
    color: color === "secondary" ? color : "inherit",
  };

  const renderDefaultIcon = (props: IconProps) => {
    switch (color) {
      case "secondary":
        return <ReportOutlinedIcon {...props} />;
      case "error":
        return <PriorityHighOutlinedIcon {...props} />;
    }
  };

  return (
    <div
      className={cx(
        classes.root,
        {
          [classes.rootCritical]: color === "critical",
          [classes.rootError]: color === "error",
          [classes.rootSecondary]: color === "secondary",
        },
        className
      )}
    >
      {renderLeft ? (
        <div className={classes.left}>{renderLeft()}</div>
      ) : (
        <div
          className={cx(classes.iconBackground, {
            [classes.iconBackgroundSecondary]: color === "secondary",
            [classes.iconBackgroundError]: color === "error",
          })}
        >
          {renderIcon ? renderIcon(iconProps) : renderDefaultIcon(iconProps)}
        </div>
      )}
      <div className={classes.message}>
        <Typography
          variant="h6"
          className={cx(classes.title, {
            [classes.titleCritical]: color === "critical",
            [classes.titleSecondary]: color === "secondary",
            [classes.titleError]: color === "error",
          })}
        >
          {title}
        </Typography>
        <Typography
          className={cx(classes.description, {
            [classes.descriptionCritical]: color === "critical",
          })}
          variant="body1"
        >
          {description}
        </Typography>
      </div>
      {!!renderButton && renderButton(buttonProps)}
      {!!onDismiss && (
        <div className={classes.dismissButtonContainer}>
          <IconButton
            aria-label="dismiss"
            className={classes.dismissButton}
            onClick={onDismiss}
            size="large"
          >
            <CloseIcon htmlColor="#C8CED8" />
          </IconButton>
        </div>
      )}
    </div>
  );
};

export default Banner;
